// AdminSeriesNewPage.tsx — (TF-6b-2; design §2, 22:00 answer 1) New series: contracts' SeriesCreate only — the pattern
// (the catalogue's, every one a draft: growth only), the title, the idea, the primary platform and the person (a growth
// account's roster character). The episode count is set at pickup and in the plan; cadence is autopilot's.
// (B117) and the audience every order of the series carries (Admin → Audiences): kept by the backend, not in SeriesCreate.
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PageContainer, TfCard, Btn } from "@/components/v2/primitives.tsx";
import { engineSeriesApi, type SeriesPatternView } from "@/api/engine-series.ts";
import { growthApi, type GrowthAccount } from "@/api/growth.ts";
import { refusalText, rememberSeries } from "@/lib/series.ts";
import { audiencesApi, type ProfileRow } from "@/api/audiences.ts";
import { SeriesAudienceSelect } from "@/components/engine/SeriesAudience.tsx";

const storage = () => { try { return window.localStorage; } catch { return null; } };   // catch-reason: no storage → no history

export function AdminSeriesNewPage() {
  const nav = useNavigate();
  const [patterns, setPatterns] = useState<SeriesPatternView[] | null>(null);
  const [accounts, setAccounts] = useState<GrowthAccount[] | null>(null);
  const [profiles, setProfiles] = useState<ProfileRow[] | null>(null);
  const [profilesErr, setProfilesErr] = useState<string | null>(null);
  const [audience, setAudience] = useState("");
  const [readErr, setReadErr] = useState<string | null>(null);
  const [patternId, setPatternId] = useState("");
  const [title, setTitle] = useState("");
  const [premise, setPremise] = useState("");
  const [primaryPlatform, setPlatform] = useState<"tiktok" | "youtube">("tiktok");
  const [castRef, setCast] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    engineSeriesApi.patterns().then((r) => { setPatterns(r.patterns); setPatternId(r.patterns[0]?.id ?? ""); })
      .catch((e) => setReadErr(`the patterns couldn't be read: ${refusalText(e)}`));   // catch-reason: shown in place of the form
    growthApi.listAccounts().then((r) => setAccounts(r.accounts.filter((a) => a.characterRegistryId)))
      .catch((e) => setReadErr(`the growth accounts couldn't be read: ${refusalText(e)}`));   // catch-reason: shown in place of the form
    audiencesApi.list().then((r) => setProfiles(r.profiles))
      .catch((e) => setProfilesErr(refusalText(e)));   // catch-reason: named under the select; the series can still be made, its audience set later
  }, []);
  const p = patterns?.find((x) => x.id === patternId);
  const needsPerson = p?.presenter === "required";
  const ready = !!p && title.trim().length > 0 && premise.trim().length >= 3 && (!needsPerson || !!castRef);
  const create = async () => {
    setBusy(true); setErr(null);
    try {
      const r = await engineSeriesApi.create({ patternId, title: title.trim(), premise: premise.trim(), primaryPlatform, castRef: castRef || null,
        audienceProfileId: audience || null });
      rememberSeries(storage(), { id: r.id, title: title.trim() });
      // (audit 10-27) made, but its audience not saved: the series' page says why (the backend's audienceError)
      nav(`/admin/series/${r.id}`, r.audienceError ? { state: { audienceError: r.audienceError } } : undefined);
    } catch (e) { setErr(refusalText(e)); }   // catch-reason: shown under the button; nothing was created
    finally { setBusy(false); }
  };
  if (readErr) return <PageContainer title="New series"><div style={{ color: "#b91c1c" }}>{readErr}</div></PageContainer>;
  if (!patterns || !accounts) return <PageContainer title="New series"><div className="tf-muted">Reading…</div></PageContainer>;
  const field = { display: "block", width: "100%", marginTop: 4, padding: 8 } as const;
  return (
    <PageContainer title="New series" subtitle="Its pilot (episode 0) is planned when it is created." maxWidth={720}>
      <TfCard>
        <label>Pattern
          <select style={field} value={patternId} onChange={(e) => setPatternId(e.target.value)}>
            {patterns.map((x) => <option key={x.id} value={x.id}>{x.name}{x.lifecycle !== "live" ? " (draft: growth only)" : ""}</option>)}
          </select></label>
        {p && <div className="tf-muted" style={{ fontSize: 13, margin: "6px 0 12px" }}>{p.explainer} Pilot {p.pilotLengthSec.min}–{p.pilotLengthSec.max} s.</div>}
        <label>Title<input style={field} value={title} maxLength={120} onChange={(e) => setTitle(e.target.value)} /></label>
        <label style={{ display: "block", marginTop: 12 }}>The idea
          <textarea style={field} rows={3} value={premise} maxLength={600} onChange={(e) => setPremise(e.target.value)} /></label>
        <label style={{ display: "block", marginTop: 12 }}>Main platform
          <select style={field} value={primaryPlatform} onChange={(e) => setPlatform(e.target.value as "tiktok" | "youtube")}>
            <option value="tiktok">TikTok</option><option value="youtube">YouTube</option>
          </select></label>
        <div className="tf-muted" style={{ fontSize: 12, marginTop: 4 }}>An episode counts once it is public here.</div>
        <label style={{ display: "block", marginTop: 12 }}>The person{needsPerson ? " (this pattern needs one who speaks)" : ""}
          <select style={field} value={castRef} onChange={(e) => setCast(e.target.value)}>
            <option value="">{needsPerson ? "Choose…" : "None"}</option>
            {accounts.map((a) => <option key={a.id} value={a.characterRegistryId!}>{a.handle}'s character</option>)}
          </select></label>
        <label style={{ display: "block", marginTop: 12 }}>The audience
          <SeriesAudienceSelect style={field} value={audience} onChange={setAudience} profiles={profiles} /></label>
        <div className="tf-muted" style={{ fontSize: 12, marginTop: 4 }}>Who the series is for: every pilot and episode order carries it. Make one in Admin → Audiences.</div>
        {profilesErr && <div style={{ color: "#b91c1c", fontSize: 13, marginTop: 4 }}>The audiences couldn't be read ({profilesErr}): set it later on the series' page.</div>}
        <div style={{ marginTop: 16 }}><Btn onClick={create} disabled={!ready || busy}>Create the series</Btn></div>
        {err && <div style={{ color: "#b91c1c", fontSize: 13, marginTop: 8 }}>{err}</div>}
      </TfCard>
    </PageContainer>
  );
}
